<script setup>
    // Common
    import i18n from '@/common/i18n';

    // Store
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useMedicalQuestionnarieStore } from '@/stores/reserve/medicalQuestionnarieStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // Vue
    import { defineEmits, defineExpose, inject, ref } from 'vue';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // Storeクラスを読み込む。
    const languageStore = useLanguageStore();
    const medicalQuestionnarieStore = useMedicalQuestionnarieStore();

    // 問診票登録モーダルの表示・非表示を管理する。
    const medicalQuestionnarieRegisterDispFlg = ref('');

    // 問診票確認モーダルの表示・非表示を管理する。
    const medicalQuestionnarieConfirmDispFlg = ref('');

    // 問診票登録完了モーダルの表示・非表示を管理る。
    const medicalQuestionnarieCompleteDispFlg = ref('');

    // APIで取得した、本モーダル内で使用する問診票フォームの構造。
    const medicalQuestionnarieForm = ref([]);

    // 本モーダル内で問診票フォームに回答した情報を保管する。
    const medicalQuestionnarieFormAnswer = ref([]);

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // 親画面から取得したAPIのドメイン部を管理する。
    const medicalQuestionnarieFormTitle = ref('');

    // 呼び出し元の親画面の問診票モーダルを閉じる処理を定義する。
    const emit = defineEmits(['medicalQuestionnarieModal:close', 'medicalQuestionnarieModal:update']);

    const keepReservationPk = ref(null);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore(); 

    // ***** 問診票登録モーダルの処理 - Start - ***** //
    /**
     * 問診票フォーム取得APIをコールし、画面の問診票登録モーダルに表示する。
     */
    async function getConsultationWebList() {
        // 変数を初期化する。
        reset();

        const queryParam = {
            Lang: languageStore.getLanguageValue,
            ReservationPk: keepReservationPk.value,
        };

        // Storeクラスを経由して問診票フォーム取得APIをコールする。
        const response = await medicalQuestionnarieStore.getMedicalQuestionnarieForm(API_URL.value, queryParam);

        if (response.success) {
            // 問診票のタイトルを格納する。
            medicalQuestionnarieFormTitle.value = utils.changeNullValueToEmpty(medicalQuestionnarieStore.medicalQuestionnarieForm.FormNm);

            // 問診票構造を加工用変数に格納する。
            const forms = JSON.parse(JSON.stringify(medicalQuestionnarieStore.medicalQuestionnarieForm.FormAttr));

            // 問診票構造が存在するか判定する。
            if (!utils.isNullEmptyArray(forms)) {
                // 問診票の各データを抽出する。
                for (const data of forms) {
                    //for (let i = 0; i < forms.length; i++) {
                    //const data = forms[i];

                    // プルダウン、チェックボックス、ラジオボタンの選択肢を作成する。
                    let ChoicesArr = [];
                    if (!utils.isNullEmptyArray(data.Choices)) {
                        for (const choice of data.Choices) {
                            let obj = {};

                            const index = choice.indexOf(':');

                            // コロンの区切りIDと名称の区切り文字が存在するか判定する。
                            if (index !== -1) {
                                // コロンの区切り文字が存在する場合は、存在する座標が返却される。
                                // 選択肢のID部と名称部を分離する。
                                obj = {
                                    id: choice.substr(0, index), // コロンの位置までを抽出しIDとして設定する。
                                    name: choice.substr(index + 1), // コロンの座標の次の文字列以降を全て名称として抽出する。
                                    value: choice,
                                };
                            } else {
                                // コロンの区切り文字ば存在しない場合は-1が返る。
                                // APIの取得情報をそのまま格納する。
                                obj = {
                                    id: choice,
                                    name: choice,
                                    value: choice,
                                };
                            }

                            // 選択肢の情報を格納する。
                            ChoicesArr.push(obj);
                        }
                    }

                    // 画面表示用にAPIから取得したデータを加工して再格納する。
                    const model = {
                        Id: data.Id,
                        No: data.No,
                        Nm: data.Nm,
                        Type: data.Type,
                        Choices: ChoicesArr,
                        Required: data.Required,
                        Explain: data.Explain,
                    };

                    // 画面に表示する問診票のフォーム構造を生成する。
                    medicalQuestionnarieForm.value.push(model);

                    // 回答用情報の格納フィールドを初期化する。
                    let Values = null;

                    // 問診票項目の起票内容に応じて、次の通り、画面フォームからの入力内容を格納するモデルを作成する。
                    switch (data.Type) {
                        case '01':
                        case '02':
                            // ラジオボックスの場合。
                            // チェックボックスの場合。
                            Values = [];
                            break;
                        default:
                            // 上記以外の場合。
                            // テキストボックスなど。
                            Values = '';
                            break;
                    }

                    // 回答用構造を設定する。
                    const answerForm = {
                        Id: data.Id,
                        Nm: data.Nm,
                        Type: data.Type,
                        Values: Values,
                        Required: data.Required,
                    };

                    // 回答用の問診票のフォーム構造を生成する。
                    medicalQuestionnarieFormAnswer.value.push(answerForm);
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    } // #getConsultationWebList

    /**
     * バリデーションチェックを行い、問診票フォーム登録APIをコールする。
     */
    async function checkTheFormAndMoveToMedicalQuestionConfirmModal() {
        // 回答記録構造を初期化する。
        medicalQuestionnarieStore.medicalQuestionnarieFormAnswer = [];

        // APIに送信するフォーム情報を作成する
        let requestBody = {
            ReservationPk: keepReservationPk.value,
            FormPk: medicalQuestionnarieStore.medicalQuestionnarieForm.FormPk,
            FormData: [],
        };

        let message = '';

        for (let i = 0; i < medicalQuestionnarieFormAnswer.value.length; i++) {
            if (medicalQuestionnarieFormAnswer.value[i].Required) {
                switch (medicalQuestionnarieFormAnswer.value[i].Type) {
                    case '01':
                    case '02':
                    case '03':
                        // ラジオボタン
                        // チェックボックス
                        // プルダウン
                        if (utils.isEmptyArray(medicalQuestionnarieFormAnswer.value[i].Values)) {
                            //message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('medicalQuestionnarieApply.message.message1') + '\n';
                            message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('common.message.formErrorMessage1') + '\n';
                        }
                        break;

                    default:
                        // 上記以外
                        if (utils.isNullEmptyBlank(medicalQuestionnarieFormAnswer.value[i].Values)) {
                            //message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('medicalQuestionnarieApply.message.message2') + '\n';
                            message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('common.message.formErrorMessage2') + '\n';
                        }
                        break;
                }
            }

            // フォーマットチェック
            switch (medicalQuestionnarieFormAnswer.value[i].Type) {
                case '04':
                    // テキストボックス
                    if (!utils.isNullEmptyBlank(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        // 文字数超過を判定
                        if (utils.checkLengthOfString(medicalQuestionnarieFormAnswer.value[i].Values)) {
                            message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('common.message.formErrorMessage3') + '\n';
                        }
                    }
                    break;
                case '05':
                    // テキストエリア
                    if (!utils.isNullEmptyBlank(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        // 文字数超過を判定
                        if (utils.checkLengthOfLongString(medicalQuestionnarieFormAnswer.value[i].Values)) {
                            message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('common.message.formErrorMessage4') + '\n';
                        }
                    }
                    break;
                case '08':
                    // 数値
                    if (!utils.isNullEmptyBlank(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        // int32の範囲超過を判定
                        if (utils.checkLengthOfDisplayOrderNumber(medicalQuestionnarieFormAnswer.value[i].Values)) {
                            message += medicalQuestionnarieFormAnswer.value[i].Nm + i18n.global.t('common.message.formErrorMessage5') + '\n';
                        }
                    }
                    break;
                default:
                    // 何もしない
                    break;
            }

            let Values = [];

            // 画面の回答内容に応じて、登録API用のリクエストボディを生成する。
            switch (medicalQuestionnarieFormAnswer.value[i].Type) {
                case '01':
                    // ラジオボタン
                    // 空ではなく、何かしら選択されている場合。
                    if (!utils.isEmptyArray(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        Values.push(medicalQuestionnarieFormAnswer.value[i].Values);
                    }
                    break;
                case '02':
                    // チェックボックス
                    // 空ではなく、何かしら選択されている場合。複数の選択を許容する。
                    if (!utils.isEmptyArray(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        for (let j = 0; j < medicalQuestionnarieFormAnswer.value[i].Values.length; j++) {
                            Values.push(medicalQuestionnarieFormAnswer.value[i].Values[j]);
                        }
                    }
                    break;
                case '07':
                    // 日付の場合。
                    // 空ではなく、何かしら入力されている場合。
                    if (!utils.isNullEmpty(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        Values.push(convertDateHyphenToSlash(medicalQuestionnarieFormAnswer.value[i].Values));
                    }
                    break;
                case '09':
                    // 見出し
                    // 09の見出しは、表示されている見出し文字列を格納する。
                    Values.push(medicalQuestionnarieFormAnswer.value[i].Nm);
                    break;
                default:
                    // 上記以外。
                    // 空ではなく、何かしら入力されている場合。
                    if (!utils.isNullEmpty(medicalQuestionnarieFormAnswer.value[i].Values)) {
                        Values.push(medicalQuestionnarieFormAnswer.value[i].Values);
                    }
                    break;
            }

            const FormData = {
                Id: medicalQuestionnarieFormAnswer.value[i].Id,
                Values: Values,
            };
            requestBody['FormData'].push(FormData);
        }

        // エラーメッセージが損じする場合は、先に進ませない。
        if (message !== '') {
            messageDialog('error', message);
            return;
        }

        // Storeに保存。
        medicalQuestionnarieStore.medicalQuestionnarieFormAnswer = requestBody;

        medicalQuestionnarieRegisterDispFlg.value = '';
        medicalQuestionnarieConfirmDispFlg.value = 'block';
        medicalQuestionnarieCompleteDispFlg.value = '';
    } // #checkTheFormAndMoveToMedicalQuestionConfirmModal
    /**
     * date(日付)タイプをyyyy-mm-ddからyyyy/mm/ddに変換する
     * @param input yyyy-mm-dd
     */
    function convertDateHyphenToSlash(input) {
        if (input !== '') {
            const [year, month, day] = input.split('-');
            return `${year}/${month}/${day}`;
        } else {
            return '';
        }
    }
    // ***** 問診票登録モーダルの処理 - End - ***** //

    // ***** 問診票登録モーダルの処理 - Start - ***** //
    /**
     * 問診票確認モーダルの「戻る」ボタンのクリックイベント。
     * 問診票登録モーダルに戻る。
     */
    function backToMedicalQuestionApplyModal() {
        medicalQuestionnarieRegisterDispFlg.value = 'block';
        medicalQuestionnarieConfirmDispFlg.value = '';
        medicalQuestionnarieCompleteDispFlg.value = '';
    } // #backToMedicalQuestionApplyModal

    /**
     * 問診票フォーム登録APIをコールし、画面の問診票完了モーダルを表示する。
     */
    async function applyTheFormAndMoveToMedicalQuestionCompleteModal() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const response = await medicalQuestionnarieStore.putMedicalQuestionnarieForm(API_URL.value, medicalQuestionnarieStore.medicalQuestionnarieFormAnswer);
        if (response.success) {
            //messageDialog('success', '登録は正常に完了しました。');
            medicalQuestionnarieRegisterDispFlg.value = '';
            medicalQuestionnarieConfirmDispFlg.value = '';
            medicalQuestionnarieCompleteDispFlg.value = 'block';

            // 初期化する。
            reset();

            // 親画面の更新処理を呼び出す。
            // emit('medicalQuestionnarieModal:update');
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    } // #applyTheFormAndMoveToMedicalQuestionCompleteModal
    // ***** 問診票登録確認モーダルの処理 - End - ***** //

    // ***** 各問診票モーダルの共通的な処理 - Start - ***** //
    /**
     * 問診票登録モーダルの「閉じる」ボタンイベント。
     * 問診票モーダルを閉じて、親画面に戻る。
     */
    function closeMedicalQuestionModal() {
        // 親画面のキャンセル処理を呼び出す。
        emit('medicalQuestionnarieModal:close');
    } // #closeMedicalQuestionModal
    /**
     * 問診票完了モーダルの「閉じる」ボタンイベント。
     * 問診票モーダルを閉じて、親画面に戻り、予約詳細再取得。
     */
    function updateMedicalQuestionModal() {
        // 親画面の更新処理を呼び出す。
        emit('medicalQuestionnarieModal:update');
    } // #updateMedicalQuestionModal

    /**
     * 親画面から問診票登録モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function openModal(trg, reservationPk) {
        // 変数を初期化する。
        reset();

        // モーダルの表示を初期化する。登録モーダルの表示に切り替えて、他モーダルは全て閉じる。
        medicalQuestionnarieRegisterDispFlg.value = 'block';
        medicalQuestionnarieConfirmDispFlg.value = '';
        medicalQuestionnarieCompleteDispFlg.value = '';

        // APIのURL情報を取得する。
        API_URL.value = trg;

        // 親画面から取得した予約PKを保持する。
        keepReservationPk.value = reservationPk;

        // Web相談一覧取得をコールする。
        await getConsultationWebList();

        
        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalMonshinhyouTourku .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    } // #openModal

    /**
     * 本モーダル内の各変数と、Storeクラス内の関連変数を初期化する。
     */
    function reset() {
        // Storeクラス内の変数を初期化する。
        medicalQuestionnarieStore.reset();
        //medicalQuestionnarieStore.medicalQuestionnarieForm = [];
        //medicalQuestionnarieStore.medicalQuestionnarieFormAnswer = [];

        // 本コンポーネント内の変数を初期化する。
        medicalQuestionnarieFormAnswer.value = [];
        medicalQuestionnarieForm.value = [];
    } // #reset

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        openModal,
    });
    // ***** 各問診票モーダルの共通的な処理 - Start - ***** //
</script>
<template>
    <article id="modalMonshinhyouTourku" class="modal" :style="{ display: medicalQuestionnarieRegisterDispFlg }">
        <h3 class="textCenter">{{ i18n.global.t('medicalQuestionnarieApply.parts.modalTitle1') }}</h3>
        <section class="modalContents double">
            <span class="surveyTitle">{{ medicalQuestionnarieFormTitle }}</span>
            <table class="survey medicalQuestion">
                <colgroup>
                    <col span="3" class="group1" />
                    <col span="1" class="group2" />
                </colgroup>
                <tbody>
                    <tr v-for="(FormAttr, index) in medicalQuestionnarieForm" :key="index">
                        <td>
                            <span>{{ FormAttr.No }}</span>
                        </td>
                        <td>
                            <span>{{ FormAttr.Nm }}</span>
                        </td>
                        <td>
                            <template v-if="FormAttr.Type === '01'">
                                <!--ラジオボタンを生成-->
                                <ul class="blockInfoBox">
                                    <li v-for="(data, index2) in FormAttr.Choices" :key="index + '-' + index2">
                                        <input type="radio" :id="'medical-questionnarie-radio-' + (index + 1) + '-' + (index2 + 1)" class="radio" :name="'medical-questionnarie-radioGroup-' + (index + 1)" v-model="medicalQuestionnarieFormAnswer[index].Values" :value="data.id" />
                                        <label class="radio label" :for="'medical-questionnarie-radio-' + (index + 1) + '-' + (index2 + 1)">{{ data.name }}</label>
                                    </li>
                                </ul>
                            </template>
                            <template v-else-if="FormAttr.Type === '02'">
                                <!--チェックボックスを生成-->
                                <ul class="blockInfoBox">
                                    <li v-for="(data, index2) in FormAttr.Choices" :key="index + '-' + index2">
                                        <input type="checkbox" :id="'medical-questionnarie-checkbox-' + (index + 1) + '-' + (index2 + 1)" class="checkbox" v-model="medicalQuestionnarieFormAnswer[index].Values" :value="data.id" />
                                        <label class="checkbox label" :for="'medical-questionnarie-checkbox-' + (index + 1) + '-' + (index2 + 1)">{{ data.name }}</label>
                                    </li>
                                </ul>
                            </template>
                            <template v-else-if="FormAttr.Type === '03'">
                                <!--ドロップダウンリストを生成-->
                                <ul class="blockInfoBox">
                                    <li style="width: 100%">
                                        <div class="select">
                                            <select v-model="medicalQuestionnarieFormAnswer[index].Values" required>
                                                <option value="">{{ $t('common.parts.selectOptionDefault') }}</option>
                                                <option v-for="(data, index2) in FormAttr.Choices" :key="index + '-' + index2" :value="data.id">{{ data.name }}</option>
                                            </select>
                                        </div>
                                    </li>
                                </ul>
                            </template>
                            <template v-else-if="FormAttr.Type === '04'">
                                <!--テキストボックスを生成-->
                                <input type="text" v-model="medicalQuestionnarieFormAnswer[index].Values" />
                            </template>
                            <template v-else-if="FormAttr.Type === '05'">
                                <!--テキストエリアを生成-->
                                <textarea v-model="medicalQuestionnarieFormAnswer[index].Values" style="height: 68px" />
                            </template>
                            <template v-else-if="FormAttr.Type === '07'">
                                <!--日付を生成-->
                                <label class="calendar">
                                    <input type="date" :class="{ blank: utils.isNullEmpty(medicalQuestionnarieFormAnswer[index].Values) }" :placeholder="$t('common.parts.selectDateDefault')" v-model="medicalQuestionnarieFormAnswer[index].Values" />
                                </label>
                            </template>
                            <template v-else-if="FormAttr.Type === '08'">
                                <!--整数値を生成-->
                                <input type="number" v-model="medicalQuestionnarieFormAnswer[index].Values" />
                            </template>
                            <template v-else-if="FormAttr.Type === '09'">
                                <!--見出しを生成-->
                                <span>{{ FormAttr.Nm }}</span>
                            </template>
                        </td>
                        <td>
                            <span>{{ FormAttr.Explain }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="closeMedicalQuestionModal">{{ i18n.global.t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" @click="checkTheFormAndMoveToMedicalQuestionConfirmModal">{{ i18n.global.t('common.parts.entry') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--問診登録入力モーダル（相談前アンケート）-->
    <article id="modalMonshinhyouTourkuCheck" class="modal small" :style="{ display: medicalQuestionnarieConfirmDispFlg }">
        <p class="textCenter">{{ $t('medicalQuestionnaireConfirm.parts.modalTitle1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="backToMedicalQuestionApplyModal">{{ $t('medicalQuestionnaireConfirm.parts.modalButton2') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" @click="applyTheFormAndMoveToMedicalQuestionCompleteModal" :disabled="disabledStore.isDisabled">{{ $t('medicalQuestionnaireConfirm.parts.modalButton1') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--問診登録確認モーダル（相談前アンケート）-->
    <article id="modalMonshinhyouTourkuComplete" class="modal small" :style="{ display: medicalQuestionnarieCompleteDispFlg }">
        <p class="textCenter">{{ $t('medicalQuestionnaireComplete.parts.modalTitle1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="updateMedicalQuestionModal">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--問診登録完了モーダル（相談前アンケート）-->
</template>

<style></style>
